/**
 * VehicleInfo Component
 * 
 * Displays vehicle/transport information.
 * Uses normalized data from QuotationFormatter.getVehicleData()
 * 
 * Usage:
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   const vehicleData = formatter.getVehicleData();
 *   
 *   <VehicleInfo
 *     data={vehicleData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Car } from 'lucide-react';
import { COMPONENT_VARIANTS, formatCurrency } from './displayConfig';

const VehicleInfo = ({ 
  data = {},
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  showPricing = true,
  className = ''
}) => {
  if (!data || !data.vehicleType) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;
  const { vehicleType, ratePerDay, ratePerDayRaw, days, total, totalRaw, vehicleCount } = data;

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="vehicle-info"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <Car className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Vehicle & Transport
        </h3>
      )}
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className={`${styles.headerBg} ${styles.headerText} px-4 py-2 font-semibold`}>
          Transfer Details
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Vehicle Type */}
            <div>
              <p className={`${styles.fontSize} text-gray-500 mb-1`}>Vehicle Type</p>
              <p className={`${styles.fontSize} font-semibold text-gray-900`}>
                {vehicleType}
              </p>
            </div>
            
            {/* Number of Vehicles */}
            <div>
              <p className={`${styles.fontSize} text-gray-500 mb-1`}>No. of Vehicles</p>
              <p className={`${styles.fontSize} font-semibold text-gray-900`}>
                {vehicleCount}
              </p>
            </div>
            
            {/* Transfer Days */}
            <div>
              <p className={`${styles.fontSize} text-gray-500 mb-1`}>Transfer Days</p>
              <p className={`${styles.fontSize} font-semibold text-gray-900`}>
                {days} days
              </p>
            </div>
            
            {/* Rate per Day (if showing pricing) */}
            {showPricing && ratePerDayRaw > 0 && (
              <div>
                <p className={`${styles.fontSize} text-gray-500 mb-1`}>Rate/Day</p>
                <p className={`${styles.fontSize} font-semibold text-gray-900`}>
                  {ratePerDay}
                </p>
              </div>
            )}
          </div>
          
          {/* Total (if showing pricing) */}
          {showPricing && totalRaw > 0 && (
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <span className={`${styles.fontSize} font-medium text-gray-700`}>
                Total Transport Cost
              </span>
              <span className={`${styles.fontSize} font-bold text-[var(--navy)]`}>
                {total}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleInfo;
